import type { KeyboardLightingEffect, RGB, keyMapLighting } from '@rapoo/device-sdk'

const BLUE: RGB = { r: 0x00, g: 0x00, b: 0xff }
const WHITE: RGB = { r: 0xff, g: 0xff, b: 0xff }

const RANDOM_COLOR_ARRAY: RGB[] = [
  { r: 0xff, g: 0x00, b: 0x00 },
  { r: 0xff, g: 0x5c, b: 0x00 },
  { r: 0xfa, g: 0xff, b: 0x00 },
  { r: 0x14, g: 0xff, b: 0x00 },
  { r: 0x00, g: 0xff, b: 0xd1 },
  { r: 0x00, g: 0xc2, b: 0xff },
  { r: 0x24, g: 0x00, b: 0xff },
  { r: 0xeb, g: 0x00, b: 0xff },
]

const WHITE_POSITIONS_BY_ROW: Record<number, number[]> = {
  0: [2, 3, 4, 5],
  1: [11, 12, 13, 14],
  3: [6, 7, 8, 9],
  5: [4],
}

const MOBA_HIGHLIGHT_HID_CODES = new Set([
  0x29, // Esc
  0x35, // `~
  0x1e, // 1
  0x1f, // 2
  0x20, // 3
  0x21, // 4
  0x22, // 5
  0x23, // 6
  0x2c, // Space
  0x2b, // Tab
  0xe0, // Left Ctrl
  0xe2, // Left Alt
  0x14, // Q
  0x1a, // W
  0x08, // E
  0x15, // R
  0x07, // D
  0x09, // F
  0x0a, // G
  0x19, // V
  0x05, // B
])

/** 静态模式 FPS：12345 / QWERASDGB / Space / Tab / F1-F3 / Ctrl_L / Shift_L / Alt_L */
const FPS_HIGHLIGHT_HID_CODES = new Set([
  0x1e, // 1
  0x1f, // 2
  0x20, // 3
  0x21, // 4
  0x22, // 5
  0x14, // Q
  0x1a, // W
  0x08, // E
  0x15, // R
  0x04, // A
  0x16, // S
  0x07, // D
  0x0a, // G
  0x05, // B
  0x2c, // Space
  0x2b, // Tab
  0x3a, // F1
  0x3b, // F2
  0x3c, // F3
  0xe0, // Left Ctrl
  0xe1, // Left Shift
  0xe2, // Left Alt
])

/** 静态模式 CAR：ASDWR / Ctrl_L / Shift_L / Alt_L / ↑↓←→ */
const CAR_HIGHLIGHT_HID_CODES = new Set([
  0x04, // A
  0x16, // S
  0x07, // D
  0x1a, // W
  0x15, // R
  0xe0, // Left Ctrl
  0xe1, // Left Shift
  0xe2, // Left Alt
  0x52, // ↑
  0x51, // ↓
  0x50, // ←
  0x4f, // →
])

type PresetAlgorithm =
  | 'random'
  | 'left_to_right_gradient'
  | 'top_to_bottom_gradient'
  | 'blue_sky'
  | 'moba'
  | 'fps'
  | 'car'

/**
 * 静态构图 / 渐变预设算法别名。
 * 炫彩预览不再走这里：由 keyboardPanel 根据 isColorful 直接使用 `random`。
 */
const PRESET_ALIASES: Record<string, PresetAlgorithm> = {
  'lighting.effect.blue_sky_white_clouds': 'blue_sky',
  'lighting.effect.row_gradient': 'left_to_right_gradient',
  'lighting.effect.col_gradient': 'top_to_bottom_gradient',
  'lighting.effect.moba': 'moba',
  'lighting.effect.fps': 'fps',
  'lighting.effect.car': 'car',
  random: 'random',
  left_to_right_gradient: 'left_to_right_gradient',
  row_gradient: 'left_to_right_gradient',
  top_to_bottom_gradient: 'top_to_bottom_gradient',
  col_gradient: 'top_to_bottom_gradient',
  blue_sky: 'blue_sky',
  moba: 'moba',
  fps: 'fps',
  car: 'car',
}

export interface PresetEffectKey {
  index: number
  keyCode?: string
  label?: string
  code?: string | number
  left?: number
  top?: number
  width?: number
  height?: number
}

interface NormalizedPresetEffectKey extends PresetEffectKey {
  hidCode: number | null
}

interface RowModel {
  row: number
  keys: NormalizedPresetEffectKey[]
}

export interface ResolvePresetEffectOptions {
  effect: string | Pick<KeyboardLightingEffect, 'i18nKey'>
  keys: PresetEffectKey[]
  gradientColors?: RGB[]
}

function clampToByte(value: number): number {
  if (value < 0) return 0
  if (value > 255) return 255
  return Math.round(value)
}

function normalizeRgb(rgb: RGB): RGB {
  return {
    r: clampToByte(rgb.r),
    g: clampToByte(rgb.g),
    b: clampToByte(rgb.b),
  }
}

function parseHidCode(value?: string | number): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (!value) return null

  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return null

  const isHex = trimmed.startsWith('0x')
  const parsed = Number.parseInt(trimmed, isHex ? 16 : 10)
  return Number.isNaN(parsed) ? null : parsed
}

function normalizeKeys(keys: PresetEffectKey[]): NormalizedPresetEffectKey[] {
  return [...keys]
    .sort((a, b) => a.index - b.index)
    .map((key) => ({
      ...key,
      hidCode: parseHidCode(key.code),
    }))
}

/**
 * 依据像素纵向位置（top）将键位聚合为「视觉行」，行内按像素横向位置（left）排序。
 * 用于需要按行处理的预设效果（如蓝天白云），不再依赖矩阵 row/col。
 */
function buildVisualRows(keys: NormalizedPresetEffectKey[]): RowModel[] {
  const groups = new Map<number, NormalizedPresetEffectKey[]>()
  for (const key of keys) {
    const top = Number.isFinite(key.top) ? Math.round(key.top as number) : 0
    const bucket = groups.get(top)
    if (bucket) {
      bucket.push(key)
    } else {
      groups.set(top, [key])
    }
  }

  return [...groups.entries()]
    .sort(([topA], [topB]) => topA - topB)
    .map(([, rowKeys], rowIndex) => ({
      row: rowIndex,
      keys: [...rowKeys].sort((a, b) => {
        const leftA = Number.isFinite(a.left) ? (a.left as number) : 0
        const leftB = Number.isFinite(b.left) ? (b.left as number) : 0
        return leftA - leftB
      }),
    }))
}

function toKeyMapItem(key: PresetEffectKey, rgb: RGB): keyMapLighting[number] {
  return {
    index: key.index,
    rgb: normalizeRgb(rgb),
  }
}

function interpolateColor(start: RGB, end: RGB, ratio: number): RGB {
  const safeRatio = Math.max(0, Math.min(1, ratio))
  return {
    r: clampToByte(start.r + (end.r - start.r) * safeRatio),
    g: clampToByte(start.g + (end.g - start.g) * safeRatio),
    b: clampToByte(start.b + (end.b - start.b) * safeRatio),
  }
}

function resolveEffectName(effect: ResolvePresetEffectOptions['effect']): string {
  return typeof effect === 'string' ? effect : effect.i18nKey
}

function resolveAlgorithm(effect: ResolvePresetEffectOptions['effect']): PresetAlgorithm | null {
  const normalized = resolveEffectName(effect).trim().toLowerCase()
  return PRESET_ALIASES[normalized] ?? null
}

function resolveGradientColors(colors?: RGB[]): { start: RGB; end: RGB } {
  const start = colors?.[0] ?? WHITE
  const end = colors?.[colors.length > 1 ? colors.length - 1 : 0] ?? BLUE
  return { start, end }
}

function hasHorizontalLayout(keys: NormalizedPresetEffectKey[]): boolean {
  return keys.every(
    (key) =>
      typeof key.left === 'number' &&
      Number.isFinite(key.left) &&
      typeof key.width === 'number' &&
      Number.isFinite(key.width),
  )
}

function hasVerticalLayout(keys: NormalizedPresetEffectKey[]): boolean {
  return keys.every(
    (key) =>
      typeof key.top === 'number' &&
      Number.isFinite(key.top) &&
      typeof key.height === 'number' &&
      Number.isFinite(key.height),
  )
}

/**
 * 计算键位在指定轴向上的像素中心坐标
 */
function getAxisCenter(key: NormalizedPresetEffectKey, axis: 'x' | 'y'): number {
  const start = (axis === 'x' ? key.left : key.top) as number
  const size = Math.max(1, (axis === 'x' ? key.width : key.height) as number)
  return start + size / 2
}

/**
 * 计算所有键位在指定轴向上的中心坐标范围
 * 以「最小中心 → 最大中心」为区间，保证两端键位能取到纯起止色
 */
function getCenterRange(
  keys: NormalizedPresetEffectKey[],
  axis: 'x' | 'y',
): { min: number; span: number } {
  const centers = keys.map((key) => getAxisCenter(key, axis))
  const min = Math.min(...centers)
  const max = Math.max(...centers)
  return { min, span: Math.max(1, max - min) }
}

function buildRandomColorKeyMap(keys: NormalizedPresetEffectKey[]): keyMapLighting {
  const segmentCount = RANDOM_COLOR_ARRAY.length

  if (hasHorizontalLayout(keys)) {
    const minLeft = Math.min(...keys.map((key) => key.left as number))
    const maxRight = Math.max(
      ...keys.map((key) => (key.left as number) + Math.max(1, key.width as number)),
    )
    const totalWidth = Math.max(1, maxRight - minLeft)

    return keys.map((key) => {
      const left = key.left as number
      const width = Math.max(1, key.width as number)
      const center = left + width / 2
      const ratio = Math.min(Math.max((center - minLeft) / totalWidth, 0), 0.999999)
      const colorIndex = Math.min(segmentCount - 1, Math.floor(ratio * segmentCount))
      return toKeyMapItem(key, RANDOM_COLOR_ARRAY[colorIndex])
    })
  }

  // 兜底：缺少像素布局时，按键位在序列中的顺序分段着色（仍不使用 row/col）
  const span = Math.max(1, keys.length)
  return keys.map((key, idx) => {
    const colorIndex = Math.min(segmentCount - 1, Math.floor((idx / span) * segmentCount))
    return toKeyMapItem(key, RANDOM_COLOR_ARRAY[colorIndex])
  })
}

/**
 * 左→右渐变
 * 不再依赖矩阵 row/col，改用键位的像素水平中心位置计算渐变比例，
 * 这样无论 index 与物理坐标如何映射，预览都能与键盘实际布局对应。
 */
function buildLeftToRightGradientKeyMap(
  keys: NormalizedPresetEffectKey[],
  startColor: RGB,
  endColor: RGB,
): keyMapLighting {
  if (!hasHorizontalLayout(keys)) {
    return buildOrdinalGradientKeyMap(keys, startColor, endColor)
  }

  const { min, span } = getCenterRange(keys, 'x')
  return keys.map((key) => {
    const ratio = (getAxisCenter(key, 'x') - min) / span
    return toKeyMapItem(key, interpolateColor(startColor, endColor, ratio))
  })
}

/**
 * 上→下渐变
 * 同样改用键位的像素垂直中心位置计算渐变比例。
 * 同一视觉行的键 top 相同，会落到相同比例，自然形成逐行的色带。
 */
function buildTopToBottomGradientKeyMap(
  keys: NormalizedPresetEffectKey[],
  startColor: RGB,
  endColor: RGB,
): keyMapLighting {
  if (!hasVerticalLayout(keys)) {
    return buildOrdinalGradientKeyMap(keys, startColor, endColor)
  }

  const { min, span } = getCenterRange(keys, 'y')
  return keys.map((key) => {
    const ratio = (getAxisCenter(key, 'y') - min) / span
    return toKeyMapItem(key, interpolateColor(startColor, endColor, ratio))
  })
}

/**
 * 兜底：缺少像素布局时，按键位在序列中的顺序均匀过渡（仍不使用 row/col）
 */
function buildOrdinalGradientKeyMap(
  keys: NormalizedPresetEffectKey[],
  startColor: RGB,
  endColor: RGB,
): keyMapLighting {
  const span = Math.max(1, keys.length - 1)
  return keys.map((key, idx) =>
    toKeyMapItem(key, interpolateColor(startColor, endColor, idx / span)),
  )
}

function buildBlueSkyKeyMap(rows: RowModel[]): keyMapLighting {
  const result: keyMapLighting = []

  rows.forEach((row, rowIndex) => {
    const whitePositions = new Set(WHITE_POSITIONS_BY_ROW[rowIndex] ?? [])
    row.keys.forEach((key, keyIndex) => {
      const serial = keyIndex + 1
      result.push(toKeyMapItem(key, whitePositions.has(serial) ? WHITE : BLUE))
    })
  })

  return result
}

function buildGameModeKeyMap(
  keys: NormalizedPresetEffectKey[],
  highlightCodes: Set<number>,
): keyMapLighting {
  return keys.map((key) => {
    const isHighlight = key.hidCode !== null && highlightCodes.has(key.hidCode)
    return toKeyMapItem(key, isHighlight ? BLUE : WHITE)
  })
}

export function setKeyboardLightingRandomColor(keys: PresetEffectKey[]): keyMapLighting {
  return buildRandomColorKeyMap(normalizeKeys(keys))
}

export function setKeyboardLeftToRightGradient(
  keys: PresetEffectKey[],
  startColor: RGB,
  endColor: RGB,
): keyMapLighting {
  return buildLeftToRightGradientKeyMap(normalizeKeys(keys), startColor, endColor)
}

export function setKeyboardTopToBottomGradient(
  keys: PresetEffectKey[],
  startColor: RGB,
  endColor: RGB,
): keyMapLighting {
  return buildTopToBottomGradientKeyMap(normalizeKeys(keys), startColor, endColor)
}

export function setKeyboardBlueSky(keys: PresetEffectKey[]): keyMapLighting {
  return buildBlueSkyKeyMap(buildVisualRows(normalizeKeys(keys)))
}

export function setKeyboardMOBA(keys: PresetEffectKey[]): keyMapLighting {
  return buildGameModeKeyMap(normalizeKeys(keys), MOBA_HIGHLIGHT_HID_CODES)
}

export function setKeyboardFPS(keys: PresetEffectKey[]): keyMapLighting {
  return buildGameModeKeyMap(normalizeKeys(keys), FPS_HIGHLIGHT_HID_CODES)
}

export function setKeyboardCAR(keys: PresetEffectKey[]): keyMapLighting {
  return buildGameModeKeyMap(normalizeKeys(keys), CAR_HIGHLIGHT_HID_CODES)
}

export function hasPresetEffect(effect: ResolvePresetEffectOptions['effect']): boolean {
  return resolveAlgorithm(effect) !== null
}

export function resolvePresetEffectKeyMap(
  options: ResolvePresetEffectOptions,
): keyMapLighting | null {
  const { keys, gradientColors } = options
  if (!keys.length) return []

  const algorithm = resolveAlgorithm(options.effect)
  if (!algorithm) return null

  const normalizedKeys = normalizeKeys(keys)
  const { start, end } = resolveGradientColors(gradientColors)

  switch (algorithm) {
    case 'random':
      return buildRandomColorKeyMap(normalizedKeys)
    case 'left_to_right_gradient':
      return buildLeftToRightGradientKeyMap(normalizedKeys, start, end)
    case 'top_to_bottom_gradient':
      return buildTopToBottomGradientKeyMap(normalizedKeys, start, end)
    case 'blue_sky':
      return buildBlueSkyKeyMap(buildVisualRows(normalizedKeys))
    case 'moba':
      return buildGameModeKeyMap(normalizedKeys, MOBA_HIGHLIGHT_HID_CODES)
    case 'fps':
      return buildGameModeKeyMap(normalizedKeys, FPS_HIGHLIGHT_HID_CODES)
    case 'car':
      return buildGameModeKeyMap(normalizedKeys, CAR_HIGHLIGHT_HID_CODES)
    default:
      return null
  }
}

export function usePresetEffect() {
  return {
    hasPresetEffect,
    resolvePresetEffectKeyMap,
    setKeyboardLightingRandomColor,
    setKeyboardLeftToRightGradient,
    setKeyboardTopToBottomGradient,
    setKeyboardBlueSky,
    setKeyboardMOBA,
    setKeyboardFPS,
    setKeyboardCAR,
  }
}
